import React from "react";
import { Link, Head } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";
import {
  Database,
  Search,
  GitPullRequest,
  Zap,
  ChevronRight,
  HardDrive,
  DollarSign,
  Gauge,
  CheckCircle,
  Award,
  RotateCcw,
  Waves,
  ShieldCheck,
} from "lucide-react";
import Layout from "../Layout";
import { CallToAction } from "@/components/marketing/CallToAction";
import {
  LatestArticles,
  type Article,
} from "@/components/marketing/LatestArticles";
import {
  PullRequestPreview,
  InlineCode,
} from "@/components/marketing/PullRequestPreview";
import { DiffPreview, DiffLine } from "@/components/marketing/DiffPreview";
import { SlackMessagePreview } from "@/components/marketing/SlackMessagePreview";
import { ProductRecommendationsMockup } from "@/components/marketing/ProductRecommendationsMockup";
import { ProductHowItWorks } from "@/components/marketing/ProductHowItWorks";
import { ProductGuardrails } from "@/components/marketing/ProductGuardrails";
import {
  ProductThresholds,
  type ProductGate,
} from "@/components/marketing/ProductThresholds";
import { FAQ, type FAQItem } from "@/components/marketing/FAQ";

// ── Hero ─────────────────────────────────────────────────────────────────────

const BREADCRUMB_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://infralyst.io",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "EBS Volume Optimization",
      item: "https://infralyst.io/products/ebs",
    },
  ],
});

const SOFTWARE_APP_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Infralyst EBS Volume Optimization",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Find gp2 EBS volumes across your AWS fleet and migrate them to gp3 with ready to merge Terraform PRs. Same storage, 20% cheaper.",
  url: "https://infralyst.io/products/ebs",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "0",
    highPrice: "99",
    priceCurrency: "USD",
    offerCount: 2,
    offers: [
      {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free plan with 3 PRs per workspace",
      },
      {
        "@type": "Offer",
        price: "99",
        priceCurrency: "USD",
        description: "Pro plan with unlimited PRs per workspace, billed monthly",
      },
    ],
  },
  publisher: {
    "@type": "Organization",
    name: "Infralyst",
    url: "https://infralyst.io",
  },
});

const RECOMMENDATIONS = [
  {
    name: "postgres_data",
    subtext: "us-east-1",
    size: "500 GB",
    current: "gp2",
    recommended: "gp3",
    savings: "$10/mo",
  },
  {
    name: "logs_archive",
    subtext: "eu-west-1",
    size: "2000 GB",
    current: "gp2",
    recommended: "gp3",
    savings: "$44/mo",
  },
  {
    name: "app_state",
    subtext: "us-east-1",
    size: "100 GB",
    current: "gp2",
    recommended: "gp3",
    savings: "$2/mo",
  },
];

function Hero(): React.ReactElement {
  return (
    <>
      <Head>
        <script type="application/ld+json">{BREADCRUMB_JSON_LD}</script>
        <script type="application/ld+json">{SOFTWARE_APP_JSON_LD}</script>
      </Head>
      <section className="px-4 pt-28 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 items-center">
            {/* Left: text */}
            <div>
              {/* Breadcrumb */}
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-1 text-sm text-surface-foreground/50 mb-6"
              >
                <span>Products</span>
                <ChevronRight className="size-3.5" />
                <span className="flex items-center gap-1 text-foreground font-medium">
                  <HardDrive className="size-3.5" />
                  EBS
                </span>
              </nav>

              <h1 className="mb-5 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
                EBS Volume <span className="text-primary">Optimization</span>
              </h1>
              <p className="mb-8 text-xl leading-relaxed text-surface-foreground/80">
                Infralyst finds gp2 volumes across your AWS fleet and
                generates ready to merge Terraform PRs to migrate them to gp3.
                Same storage, 20% cheaper.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Button asChild size="lg">
                  <Link href="/signup">Start free with 3 PRs</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href={CALENDLY_COST_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                    Book a demo
                  </a>
                </Button>
              </div>
              <ul className="flex flex-col gap-2">
                {["Zero downtime live migration", "Read-only IAM role", "Your team reviews and merges every change"].map((point) => (
                  <li key={point} className="flex items-center gap-2 text-sm text-surface-foreground/60">
                    <CheckCircle className="size-4 text-green-500 flex-none" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: UI mockup */}
            <div className="lg:pl-4">
              <ProductRecommendationsMockup
                title="EBS Volumes"
                icon={HardDrive}
                recommendations={RECOMMENDATIONS}
                footerText="Analyzed 62 volumes across 3 regions"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ── The Problem ───────────────────────────────────────────────────────────────

function TheProblem(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-4xl font-bold mb-6">
          You're paying 20% more for
          <br />
          EBS storage <span className="text-primary">than you need to</span>
        </h2>
        <p className="text-lg leading-relaxed text-surface-foreground/80 mb-4">
          gp3 came out in late 2020. Before that, gp2 was the default. Every
          EBS volume you provisioned back then was gp2, and unless someone
          migrated it to gp3, it still is. Those volumes are still running
          today, forgotten, because nobody ever owns old storage cleanup.
          They sit there costing 20% more than necessary.
        </p>
        <p className="text-lg leading-relaxed text-surface-foreground/80">
          AWS Trusted Advisor will flag gp2 volumes, but stops at the
          recommendation. Infralyst picks up where it leaves off: it finds
          every gp2 volume in your fleet and generates a Terraform PR your
          team can review and merge.
        </p>
      </div>
    </section>
  );
}

// ── How the Analysis Works ────────────────────────────────────────────────────

const ANALYSIS_STEPS = [
  {
    icon: Database,
    title: "1. Collect volume inventory",
    description:
      "Infralyst lists every EBS volume across your connected accounts and regions, along with its size, type, and attachment state.",
  },
  {
    icon: Search,
    title: "2. Identify gp2 volumes",
    description:
      "Filter to volumes whose type is gp2. That's the whole analysis. Simple on purpose. gp3 is a drop in replacement, so the savings are mechanical.",
  },
  {
    icon: GitPullRequest,
    title: "3. Generate the Terraform PR",
    description:
      "An AI agent locates the resource in your Terraform, flips the type to gp3, validates the change, and opens a PR you can review and merge.",
  },
] as const;

// ── Why gp3 beats gp2 ─────────────────────────────────────────────────────────

const WHY_GP3_REASONS = [
  {
    icon: DollarSign,
    title: "20% cheaper per GB",
    description:
      "gp3 is roughly 20% cheaper per GB than gp2 in every AWS region. In us-east-1 that's $0.08/GB-month versus $0.10. The savings apply the moment the migration completes. No long-term commitment, no reserved capacity to buy.",
  },
  {
    icon: Gauge,
    title: "Better baseline performance",
    description:
      "gp3 ships with 3,000 IOPS and 125 MB/s of throughput included, regardless of volume size. gp2 only hits 3,000 IOPS at 1,000 GB. Smaller gp2 volumes are throttled by design.",
  },
  {
    icon: Award,
    title: "AWS recommends it",
    description:
      "gp3 is the current generation general purpose volume type. AWS Trusted Advisor and the EBS documentation both recommend migrating gp2 volumes to gp3. It's a supported, live migration with no application changes.",
  },
] as const;

function WhyGp3(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Why <span className="text-primary">gp3</span> beats gp2
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          gp3 is a strict improvement over gp2 for general purpose workloads.
          Cheaper, faster baseline, and AWS's current generation default.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {WHY_GP3_REASONS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-2xl border bg-background/50"
            >
              <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="size-5" />
              </div>
              <h3 className="font-semibold mb-2">{title}</h3>
              <p className="text-sm text-surface-foreground/80 leading-relaxed">
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Trigger PRs from Slack ────────────────────────────────────────────────────

const SLACK_REC = RECOMMENDATIONS[1];

function TriggerFromSlack(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Trigger PRs{" "}
          <span className="text-primary">directly from Slack</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Get notified when new gp2 volumes are found. Review the details and
          generate a PR without leaving Slack.
        </p>

        <div className="max-w-xl mx-auto overflow-x-auto">
          <div className="min-w-[500px]">
            <SlackMessagePreview>
              {/* Header */}
              <p className="font-bold text-[15px] leading-snug mb-5">
                New EBS savings recommendation · Save $44.00/mo
              </p>

              {/* Metric fields */}
              <div className="grid grid-cols-2 gap-x-10 mb-1">
                <div className="mb-4">
                  <p className="font-bold text-sm">Resource</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.name} ({SLACK_REC.subtext})
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Size</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.size}
                  </p>
                </div>
              </div>

              {/* Current / Recommended row */}
              <div className="grid grid-cols-2 gap-x-10 mb-1">
                <div className="mb-4">
                  <p className="font-bold text-sm">Current</p>
                  <span className="font-mono text-sm text-surface-foreground/60">
                    {SLACK_REC.current}
                  </span>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Recommended</p>
                  <span className="font-mono text-sm text-primary font-medium">
                    {SLACK_REC.recommended}
                  </span>
                </div>
              </div>

              {/* Baseline preservation note */}
              <p className="text-xs text-surface-foreground/70 mb-3 leading-relaxed">
                ℹ️ We're setting iops = 6000 and throughput = 250 on the gp3
                volume to preserve its gp2 size-derived baseline. The estimated
                savings already account for the provisioning cost.
              </p>

              {/* Link */}
              <p className="text-xs text-primary mb-3">View on Infralyst</p>

              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button className="text-xs font-bold px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
                  Generate PR
                </button>
                <button className="text-xs font-bold px-3 py-1.5 rounded-md border border-foreground/25 text-foreground/70">
                  Dismiss
                </button>
              </div>
            </SlackMessagePreview>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── What the PR Looks Like ────────────────────────────────────────────────────

const EBS_PR_BODY = (
  <>
    <p>
      EBS Volume <InlineCode>logs_archive</InlineCode> in{" "}
      <InlineCode>eu-west-1</InlineCode> can be migrated from{" "}
      <InlineCode>gp2</InlineCode> to <InlineCode>gp3</InlineCode>, saving an
      estimated $44/mo.
    </p>

    <p>
      ℹ️ We're setting <InlineCode>iops = 6000</InlineCode> and{" "}
      <InlineCode>throughput = 250</InlineCode> on the gp3 volume to preserve
      its gp2 size-derived baseline. The estimated savings already account for
      the provisioning cost.
    </p>

    <p>Size: 2000 GB</p>

    <p>
      <a
        href="https://infralyst.io/workspaces/acme-corp/aws_accounts/mock-uuid"
        className="text-primary underline"
      >
        View on Infralyst
      </a>
    </p>
  </>
);

const EBS_DIFF_LINES: DiffLine[] = [
  {
    type: "hunk",
    content: '@@ -12,8 +12,10 @@ resource "aws_ebs_volume" "logs_archive" {',
  },
  {
    type: "context",
    content: 'resource "aws_ebs_volume" "logs_archive" {',
    oldLineNumber: 12,
    newLineNumber: 12,
  },
  {
    type: "context",
    content: '  availability_zone = "eu-west-1a"',
    oldLineNumber: 13,
    newLineNumber: 13,
  },
  {
    type: "context",
    content: "  size              = 2000",
    oldLineNumber: 14,
    newLineNumber: 14,
  },
  {
    type: "removed",
    content: '  type              = "gp2"',
    oldLineNumber: 15,
  },
  {
    type: "added",
    content: '  type              = "gp3"',
    newLineNumber: 15,
  },
  {
    type: "added",
    content: "  iops              = 6000",
    newLineNumber: 16,
  },
  {
    type: "added",
    content: "  throughput        = 250",
    newLineNumber: 17,
  },
  {
    type: "context",
    content: "  tags = {",
    oldLineNumber: 16,
    newLineNumber: 18,
  },
  {
    type: "context",
    content: '    Name = "logs-archive"',
    oldLineNumber: 17,
    newLineNumber: 19,
  },
  {
    type: "context",
    content: "  }",
    oldLineNumber: 18,
    newLineNumber: 20,
  },
  { type: "context", content: "}", oldLineNumber: 19, newLineNumber: 21 },
];

function WhatThePrLooksLike(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          What the <span className="text-primary">PR looks like</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Infralyst opens a Terraform pull request in your repo. Your team
          reviews and merges it like any other change.
        </p>

        <PullRequestPreview
          title="Migrate EBS Volume logs_archive: gp2 → gp3 (-$44/mo)"
          author="infralyst"
          status="open"
          body={EBS_PR_BODY}
          filesChangedContent={
            <DiffPreview filename="storage.tf" lines={EBS_DIFF_LINES} />
          }
        />
      </div>
    </section>
  );
}

// ── Thresholds & Methodology ──────────────────────────────────────────────────

const EBS_THRESHOLD_GATES: readonly ProductGate[] = [
  {
    gate: "IOPS baseline",
    icon: Gauge,
    checks: [
      { metric: "gp2 baseline", threshold: "3 × GiB (cap 16,000)" },
      { metric: "gp3 default", threshold: "3,000 IOPS" },
      { metric: "When gp2 > gp3", threshold: "Match on gp3" },
    ],
  },
  {
    gate: "Throughput baseline",
    icon: Waves,
    checks: [
      { metric: "gp2 baseline", threshold: "Up to 250 MiB/s" },
      { metric: "gp3 default", threshold: "125 MiB/s" },
      { metric: "When gp2 > gp3", threshold: "Match on gp3" },
    ],
  },
];

// ── Safety & Guardrails ───────────────────────────────────────────────────────

const GUARDRAILS = [
  {
    icon: Zap,
    title: "Zero downtime",
    description:
      "gp2 to gp3 is a live AWS migration. The volume stays attached and mounted. No detach, no remount, no snapshot. Workloads keep running while AWS performs the type change in the background.",
  },
  {
    icon: ShieldCheck,
    title: "Baseline preserved",
    description:
      "gp2 derives IOPS and throughput from volume size. For large volumes whose baseline exceeds gp3 defaults, the PR explicitly sets iops and throughput on the gp3 resource so performance doesn't regress. The estimated savings already account for the provisioning cost.",
  },
  {
    icon: RotateCcw,
    title: "Easy to revert",
    description:
      "Revert the PR on GitHub, or flip the type back to gp2 in Terraform. AWS handles the reverse migration the same way, live and in the background.",
  },
  {
    icon: GitPullRequest,
    title: "PR-only changes",
    description:
      "Infralyst never applies changes directly. Every recommendation is delivered as a notification in Slack or in our console. You trigger PR generation, review, test, and merge on your timeline.",
  },
] as const;

// ── FAQ ───────────────────────────────────────────────────────────────────────

const EBS_FAQ_ITEMS: readonly FAQItem[] = [
  {
    question: "What's the difference between gp2 and gp3 EBS volumes?",
    answer:
      "gp3 is the current generation general purpose SSD volume type and is roughly 20% cheaper per GB than gp2. gp3 ships with a flat 3,000 IOPS and 125 MB/s throughput at any size, while gp2 scales IOPS with size and only reaches 3,000 IOPS at 1,000 GB. For most workloads gp3 is a strict upgrade.",
  },
  {
    question: "How do you find gp2 EBS volumes across multiple AWS accounts?",
    answer:
      "Connect each account through a read-only cross-account IAM role and list volumes in every enabled region. Infralyst does this automatically across your connected accounts, returning every gp2 volume with its size, attachment state, and estimated monthly saving from a gp3 migration.",
  },
  {
    question: "Does AWS Trusted Advisor migrate gp2 volumes to gp3 automatically?",
    answer:
      "No. Trusted Advisor flags gp2 volumes as a cost optimization check but stops at the recommendation. Someone still has to open the Terraform file, change the type, and ship a PR. Infralyst closes that loop by generating the Terraform pull request for every gp2 volume it finds.",
  },
  {
    question: "Is there any workload where gp2 is still a better choice than gp3?",
    answer:
      "Not for general purpose workloads. gp3 matches gp2 on durability and block interface, costs 20% less per GB, and has a higher baseline IOPS floor. The only reason to keep gp2 is inertia. If you need more than 3,000 IOPS or 125 MB/s on gp3, provision them explicitly on the volume.",
  },
  {
    question: "Does migrating an EBS volume from gp2 to gp3 cause downtime?",
    answer:
      "No. AWS performs the type change as a live ModifyVolume operation. The volume stays attached, no detach, no remount, no snapshot, no reboot. Your application keeps running throughout. Infralyst only generates the Terraform PR, your team applies it on your normal deploy cadence.",
  },
  {
    question: "What happens to volumes that need more than 3,000 IOPS or 125 MB/s?",
    answer:
      "gp2 derives IOPS and throughput from volume size, so a large gp2 volume can have a baseline above gp3's defaults. Infralyst computes that baseline per volume from its size and region, and if it exceeds 3,000 IOPS or 125 MiB/s the generated PR sets explicit iops and throughput on the aws_ebs_volume resource to preserve it.",
  },
  {
    question: "How does Infralyst calculate the exact savings for a gp2 to gp3 migration?",
    answer:
      "Infralyst reads each volume's size and region, then prices gp2 and gp3 storage against the current AWS Price List for that region. The delta is the monthly saving. Every number you see in the dashboard, the Slack notification, and the PR body is computed per volume, not estimated from a flat percentage.",
  },
  {
    question: "What if EBS volumes are defined inside a shared Terraform module?",
    answer:
      "The Infralyst AI agent reads your Terraform repository, locates the aws_ebs_volume resource whether it lives at the root or inside a module, and changes the type at the right level. If the type comes from a shared variable that other volumes depend on, the agent overrides the type on the target resource instead of mutating the shared variable.",
  },
  {
    question: "What IAM permissions does Infralyst need to analyze EBS volumes?",
    answer:
      "A read-only cross-account IAM role. Infralyst reads EBS volume metadata (type, size, attachment state) through the EC2 API. It has no permissions to create, modify, delete, or detach volumes. Changes reach your infrastructure only through Terraform pull requests that your team reviews and merges.",
  },
  {
    question: "Is it safe to run a gp2 to gp3 migration on production databases?",
    answer:
      "Yes. gp3 has the same durability guarantees as gp2 and the ModifyVolume operation runs in the background while the volume stays mounted. For large volumes whose gp2 size-derived baseline exceeds gp3 defaults, the generated PR sets explicit iops and throughput so production databases don't lose IOPS or throughput in the migration. Infralyst generates one Terraform PR per volume, so you can stage the change on a non-critical volume first and merge the rest once you're comfortable.",
  },
  {
    question: "Could a gp2 to gp3 migration silently regress my volume's performance?",
    answer:
      "It can if you migrate a large gp2 volume to default gp3. gp2 scales IOPS at 3 per GiB and throughput up to 250 MiB/s with size, while gp3's defaults are flat at 3,000 IOPS and 125 MiB/s. Any gp2 volume above ~1 TiB or ~334 GiB has a baseline that exceeds those defaults. Infralyst's analysis computes the gp2 baseline per volume and, when it's higher than gp3 defaults, the generated PR sets explicit iops and throughput on the gp3 resource so the gp3 volume keeps the same baseline.",
  },
  {
    question: "Can I roll back a gp3 migration if something goes wrong?",
    answer:
      "Yes. Revert the Terraform change to flip the type back to gp2 and AWS runs another live ModifyVolume in reverse. No data loss, no downtime, no snapshot restore. In practice there's almost never a reason to roll back, gp3 is strictly cheaper and faster at baseline than gp2.",
  },
] as const;

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Ebs({
  articles,
}: {
  articles: Article[];
}): React.ReactElement {
  return (
    <Layout
      title="EBS Volume Optimization"
      description="Find gp2 EBS volumes across your AWS fleet and migrate them to gp3 with ready to merge Terraform PRs. Same storage, 20% cheaper."
    >
      <Hero />
      <TheProblem />
      <ProductHowItWorks steps={ANALYSIS_STEPS} />
      <WhyGp3 />
      <ProductThresholds
        subtitle="gp2 derives IOPS and throughput from volume size, while gp3 ships with flat defaults. For any gp2 volume whose size-derived baseline is higher, Infralyst preserves the baseline on the gp3 replacement automatically."
        gates={EBS_THRESHOLD_GATES}
        footer="Infralyst computes the baseline per volume from its size and region."
      />
      <WhatThePrLooksLike />
      <TriggerFromSlack />
      <ProductGuardrails
        guardrails={GUARDRAILS}
        crossLink={
          <>
            Also available for{" "}
            <Link href="/products/ec2" className="text-primary hover:underline">
              EC2 Instances
            </Link>
            ,{" "}
            <Link
              href="/products/auto-scaling-groups"
              className="text-primary hover:underline"
            >
              Auto Scaling Groups
            </Link>
            , and{" "}
            <Link
              href="/products/ecs-fargate"
              className="text-primary hover:underline"
            >
              ECS Fargate
            </Link>
            .
          </>
        }
      />
      <FAQ
        items={EBS_FAQ_ITEMS}
        title="EBS Optimization FAQ"
        highlightWord="Optimization"
        className="py-16 px-4"
      />
      <LatestArticles
        articles={articles}
        title={
          <>
            Learn more about EBS{" "}
            <span className="text-primary">optimization</span>
          </>
        }
        subtitle={null}
      />
      <CallToAction
        heading="Start optimizing your EBS volumes"
        subheading="Connect your AWS account and Infralyst will find every gp2 volume in your fleet. When you're ready, generate the Terraform PR and merge it."
      />
    </Layout>
  );
}
